import { useTranslation } from "react-i18next"

import { useNavigate } from "react-router-dom"
import { Text, EmptyState, Alert } from "@oysterhr/ui"
import { useAccountAspect } from "@oysterhr/identity/AccountAspectContext"

// flows
import {
  companyAchFlowPath,
  companySepaFlowPath,
  companyBacsFlowPath,
  companyPadFlowPath,
  companySepaPath,
  companyAchPath,
} from "@oysterhr/router"

import { CompanyCurrencyEnumType } from "@oysterhr/schema"
import { useCompanyPaymentsSettingsContext } from "../../contexts/CompanyPaymentsSettingsContext"

interface WithoutConnectedPaymentMethodProps {
  paymentCurrencyCode: CompanyCurrencyEnumType
  directDebitSupported: boolean
}

export const WithoutConnectedPaymentMethod = ({
  paymentCurrencyCode,
  directDebitSupported,
}: WithoutConnectedPaymentMethodProps) => {
  const { t } = useTranslation("company_interface/CompanyPaymentMethodsPage/LandingPage", {
    keyPrefix: "emptyState",
  })
  const { paymentsMandateSetup } = useCompanyPaymentsSettingsContext()
  const navigate = useNavigate()
  const { accountAspect: companyId } = useAccountAspect()

  const isEur = paymentCurrencyCode === CompanyCurrencyEnumType.Eur
  const isUsd = paymentCurrencyCode === CompanyCurrencyEnumType.Usd
  const isGbp = paymentCurrencyCode === CompanyCurrencyEnumType.Gbp
  const isCad = paymentCurrencyCode === CompanyCurrencyEnumType.Cad

  const handleConnectPaymentMethodButton = () => {
    if (isUsd) {
      paymentsMandateSetup
        ? navigate(companyAchPath(companyId))
        : navigate(companyAchFlowPath(companyId))
    } else if (isEur) {
      paymentsMandateSetup
        ? navigate(companySepaPath(companyId))
        : navigate(companySepaFlowPath(companyId))
    } else if (isGbp) {
      navigate(companyBacsFlowPath(companyId))
    } else if (isCad) {
      navigate(companyPadFlowPath(companyId))
    }
  }

  return (
    <div className="flex justify-center">
      <EmptyState className="w-1/2">
        <EmptyState.Heading>{t("title")}</EmptyState.Heading>
        <EmptyState.Content>
          <Text>{t("description")}</Text>

          <Alert variation="gray" className="my-4">
            <Alert.Icon />
            <Alert.Content className="text-left">{t("alert.currentPaymentWay")}</Alert.Content>
          </Alert>

          {!directDebitSupported && (
            <Alert variation="warning" className="my-4 text-left">
              <Alert.Icon />
              <Alert.Heading>{t("alert.notSupported.heading")}</Alert.Heading>
              <Alert.Content>
                {t("alert.notSupported.content", {
                  currency: paymentCurrencyCode,
                })}
              </Alert.Content>
            </Alert>
          )}
        </EmptyState.Content>
        <EmptyState.Actions>
          <EmptyState.ActionButton
            data-analytics-name="company_payment_methods-connect-payment-method"
            onClick={handleConnectPaymentMethodButton}
          >
            {t("button")}
          </EmptyState.ActionButton>
        </EmptyState.Actions>
      </EmptyState>
    </div>
  )
}
